import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Container,
  Row,
  Col,
  Card,
  CardBody,
} from 'reactstrap';
import PromotionRow from '../../components/campaign/promotionRow';
import { useDispatch, useSelector } from 'react-redux';
import { BeatLoader } from 'react-spinners';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import NoRowExistsRow from '../../components/common/noRowExistsRow';
import {
  promotionListFetchData,
  redirectToPromotionListPage,
} from '../../actions/promotionActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCaretSquareUp,
  faCaretSquareDown,
  faFilter,
} from '@fortawesome/free-solid-svg-icons';
import sort from 'fast-sort';
import { useNavigate } from 'react-router-dom';

const SortColumns = {
  PROMOTION_ID: 'externalId',
  PROMOTION_NAME: 'name',
  START_DATE: 'startDate',
  END_DATE: 'endDate',
  STATUS: 'status',
};

const SortOrder = {
  Asc: 'Asc',
  Dsc: 'Dsc',
};

const PromotionListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const promotions = useSelector((state) => state.promotionList ?? []);
  const hasErrored = useSelector(
    (state) => state.promotionListHasErrored ?? false,
  );
  const isLoading = useSelector(
    (state) => state.promotionListIsLoading ?? true,
  );

  const initialFormState = {
    source: '',
    promotionName: '',
    startDate: null,
    endDate: null,
    status: '1',
  };
  const [formState, setFormState] = useState(initialFormState);

  // Dirty check: true if any form field is changed
  const isFormDirty = useMemo(
    () =>
      Object.keys(initialFormState).some(
        (key) => formState[key] !== initialFormState[key],
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formState],
  );

  const [emptyRowString, setEmptyRowString] = useState('No Row Exist');
  const [isFormValid, setIsFormValid] = useState(false);
  const [sortColumn, setSortColumn] = useState(SortColumns.PROMOTION_ID);
  const [sortOrder, setSortOrder] = useState(SortOrder.Asc);
  const [errors, setErrors] = useState({
    source: false,
    promotionName: false,
    startDate: false,
    endDate: false,
    status: false,
  });

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = { ...errors };
    let sDate = null,
      eDate = null;

    if (formState.startDate) {
      sDate = moment(formState.startDate).format('DD-MMM-YYYY');
    }
    if (formState.endDate) {
      eDate = moment(formState.endDate).format('DD-MMM-YYYY');
    }

    if (sDate && eDate) {
      const isSameOrBefore = moment(eDate, 'DD-MMM-YYYY').isSameOrBefore(
        moment(sDate, 'DD-MMM-YYYY'),
      );
      if (isSameOrBefore) {
        alert('Invalid date');
        newErrors.startDate = true;
        newErrors.endDate = true;
      } else {
        newErrors.startDate = false;
        newErrors.endDate = false;
      }
    }

    const valid = !newErrors.startDate && !newErrors.endDate;
    setIsFormValid(valid);
    setErrors(newErrors);
    return valid;
  }, [formState, errors]);

  // Memoized dispatch for fetching promotions
  const fetchPromotions = useCallback(() => {
    const startingDate = formState.startDate
      ? moment(formState.startDate).format('DD-MMM-YYYY')
      : null;
    const endingDate = formState.endDate
      ? moment(formState.endDate).format('DD-MMM-YYYY')
      : null;

    dispatch(
      promotionListFetchData(
        formState.source,
        startingDate,
        endingDate,
        formState.promotionName,
        formState.status,
      ),
    );
  }, [dispatch, formState]);

  // Fetch promotions on mount and when formState changes
  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState]);

  // Sorting promotions when promotions, sortColumn, or sortOrder changes
  const sortPromotions = (() => {
    const promotionsCopy = [...promotions]; // create a shallow copy to avoid mutation
    if (sortOrder === SortOrder.Asc) {
      return sort(promotionsCopy).asc((o) => o[sortColumn]);
    }
    if (sortOrder === SortOrder.Dsc) {
      return sort(promotionsCopy).desc((o) => o[sortColumn]);
    }
  })();

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleStartDateChange = (date) => {
    setFormState((prev) => ({ ...prev, startDate: date }));
  };

  const handleEndDateChange = (date) => {
    setFormState((prev) => ({ ...prev, endDate: date }));
  };

  const handleSubmitSearchForm = (e) => {
    e.preventDefault();
    setEmptyRowString('No data has been found for the specified selection');
    fetchPromotions();
  };

  const gotoAddNewPage = () => {
    dispatch(redirectToPromotionListPage(false));
    navigate('/promotions/add-promotion');
  };

  const getSortButton = (column) => {
    return sortColumn === column && sortOrder === SortOrder.Asc ? (
      <span>
        {' '}
        <FontAwesomeIcon icon={faCaretSquareUp} />
      </span>
    ) : (
      <span>
        {' '}
        <FontAwesomeIcon icon={faCaretSquareDown} />
      </span>
    );
  };

  const onSort = (event, sortKey) => {
    setSortColumn(sortKey);
    setSortOrder((prev) =>
      prev === SortOrder.Asc ? SortOrder.Dsc : SortOrder.Asc,
    );
  };

  const promotionRows =
    sortPromotions && sortPromotions.length > 0 ? (
      sortPromotions.map((promotion) => (
        <div className='panel panel-default' key={promotion.externalId}>
          <PromotionRow promotion={promotion} />
        </div>
      ))
    ) : !isLoading ? (
      <NoRowExistsRow emptyRowText={emptyRowString} />
    ) : (
      <></>
    );

  return (
    <div>
      <h4>Manage Promotions</h4>
      <Card>
        <CardBody>
          <Container fluid>
            <Row>
              <Col className='p-0'>
                <Form onSubmit={handleSubmitSearchForm}>
                  <div className='d-flex justify-content-left align-items-center flex-wrap gap-0'>
                    <FormGroup className='d-flex align-items-center me-2'>
                      <Label for='source' className='mb-0 me-2 '>
                        Source
                      </Label>
                      <Input
                        type='select'
                        name='source'
                        id='source'
                        onChange={handleInputChange}
                        value={formState.source}
                        className={
                          errors.source
                            ? 'form-control error mb-0'
                            : 'form-control mb-0'
                        }
                      >
                        <option value=''>All</option>
                        <option value='SB'>StarBos</option>
                        <option value='VE'>Vynamic Engage</option>
                      </Input>
                    </FormGroup>
                    <FormGroup className='d-flex align-items-center me-2'>
                      <Label for='promotionName' className='mb-0 me-2 '>
                        Promotion Name
                      </Label>
                      <Input
                        id='promotionName'
                        name='promotionName'
                        onChange={handleInputChange}
                        value={formState.promotionName}
                        className={
                          errors.promotionName
                            ? 'form-control error mb-0'
                            : 'form-control mb-0'
                        }
                        style={{ width: '205px' }}
                      />
                    </FormGroup>
                    <FormGroup className='d-flex align-items-center me-2'>
                      <Label for='startDate' className='mb-0 me-2'>
                        Start Date
                      </Label>
                      <DatePicker
                        id='startDate'
                        name='startDate'
                        selected={formState.startDate}
                        dateFormat='DD-MMM-YYYY'
                        onChange={handleStartDateChange}
                        className={
                          errors.startDate ? 'error mb-0' : 'form-control mb-0'
                        }
                        locale='en-au'
                      />
                    </FormGroup>
                    <FormGroup className='d-flex align-items-center me-2'>
                      <Label for='endDate' className='mb-0 me-2'>
                        End Date
                      </Label>
                      <DatePicker
                        id='endDate'
                        name='endDate'
                        selected={formState.endDate}
                        dateFormat='DD-MMM-YYYY'
                        onChange={handleEndDateChange}
                        className={
                          errors.endDate ? 'error mb-0' : 'form-control mb-0'
                        }
                        locale='en-au'
                      />
                    </FormGroup>
                    <FormGroup className='d-flex align-items-center me-2'>
                      <Label for='status' className='me-2 '>
                        Status
                      </Label>
                      <Input
                        type='select'
                        name='status'
                        id='status'
                        onChange={handleInputChange}
                        value={formState.status}
                        className={
                          errors.status
                            ? 'form-control error mb-0'
                            : 'form-control mb-0'
                        }
                      >
                        <option value=''>All</option>
                        <option value='1'>Active</option>
                        <option value='0'>Inactive</option>
                      </Input>
                    </FormGroup>
                  </div>
                  <div className='d-flex flex-column align-items-start gap-0'>
                    <FormGroup className='d-flex align-items-center mb-0 me-sm-2'>
                      <Button
                        color='primary'
                        name='btnFilter'
                        id='btnFilter'
                        disabled={!isFormValid || isLoading || !isFormDirty}
                      >
                        <FontAwesomeIcon icon={faFilter} />
                        Filter
                      </Button>
                    </FormGroup>
                    {promotions.length}/{promotions.length}
                    {/* <Button
                      color="primary"
                      name="btnAddNew"
                      id="btnAddNew"
                      onClick={gotoAddNewPage}
                    >
                      Add New
                    </Button> */}
                  </div>
                </Form>
              </Col>
            </Row>
          </Container>
        </CardBody>
      </Card>
      <h6 className='mt-2'>Promotions</h6>
      <Container fluid>
        <Row>
          <Col
            md='1'
            className='column-header white-border'
            onClick={(e) => onSort(e, SortColumns.PROMOTION_ID)}
          >
            Promo Id
            {getSortButton(SortColumns.PROMOTION_ID)}
          </Col>
          <Col
            md='7'
            className='column-header white-border'
            onClick={(e) => onSort(e, SortColumns.PROMOTION_NAME)}
          >
            Promotion Name
            {getSortButton(SortColumns.PROMOTION_NAME)}
          </Col>
          <Col
            md='1'
            className='column-header white-border'
            onClick={(e) => onSort(e, SortColumns.START_DATE)}
          >
            Start Date
            {getSortButton(SortColumns.START_DATE)}
          </Col>
          <Col
            md='1'
            className='column-header white-border'
            onClick={(e) => onSort(e, SortColumns.END_DATE)}
          >
            End Date
            {getSortButton(SortColumns.END_DATE)}
          </Col>
          <Col
            md='1'
            className='column-header white-border'
            onClick={(e) => onSort(e, SortColumns.STATUS)}
          >
            Status
            {getSortButton(SortColumns.STATUS)}
          </Col>
          <Col md='1' className='column-header white-border'></Col>
        </Row>
        {promotionRows}
        <Row>
          <Col>
            <BeatLoader color={'#004C5F'} loading={isLoading} />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PromotionListPage;
